import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Mail, Trash2, Copy, RefreshCw, Info, CheckCircle2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useAuthStore } from '../store/authStore';
import axios from 'axios';
import toast from 'react-hot-toast';

function Settings() {
  const { user } = useAuthStore();
  const [scanEmails, setScanEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  const SCAN_DOMAIN = process.env.REACT_APP_SCAN_EMAIL_DOMAIN || 'scan.rspamd.net';

  const getAutomaticScanEmail = () => {
    if (!user?.email) return '';
    const email = user.email.toLowerCase();
    const atIndex = email.indexOf('@');
    if (atIndex === -1) return '';
    return `${email.substring(0, atIndex)}.${email.substring(atIndex + 1)}@scan.rspamd.net`;
  };

  useEffect(() => { fetchScanEmails(); }, []);

  const fetchScanEmails = async () => {
    try { setLoading(true); const r = await axios.get('/users/scan-emails'); setScanEmails(r.data.emails || []); }
    catch { toast.error('Failed to load scan emails'); }
    finally { setLoading(false); }
  };


  const toggleScanEmail = async (id, active) => {
    try { await axios.patch(`/users/scan-email/${id}`, { is_active: !active }); toast.success(active ? 'Deactivated' : 'Activated'); fetchScanEmails(); }
    catch { toast.error('Failed to update'); }
  };

  const deleteScanEmail = async (id, email) => {
    if (!window.confirm(`Delete "${email}"?`)) return;
    try { await axios.delete(`/users/scan-email/${id}`); toast.success('Deleted'); fetchScanEmails(); }
    catch { toast.error('Failed to delete'); }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Never';

  return (
    <>
      <Helmet><title>Settings - Rspamd Email Scanner</title></Helmet>
      <div>
        <h2 className="text-2xl font-bold mb-1">Settings</h2>
        <p className="text-gray-500 mb-6">Configure your application preferences and manage scan emails</p>

        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="text-xl font-semibold">Email Scanner Management</h3>
              <p className="text-sm text-gray-500">Your scan email is automatically mapped from your OAuth email address</p>
            </div>
          </div>

          {user?.email && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="font-semibold text-green-800 mb-2">Your Personal Scan Email Address</p>
              <div className="flex items-center gap-2 mb-2">
                <code className="bg-green-100 px-3 py-1.5 rounded font-mono text-sm font-bold">{getAutomaticScanEmail()}</code>
                <button onClick={() => copyToClipboard(getAutomaticScanEmail())} className="bg-green-600 text-white p-1.5 rounded hover:bg-green-700"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="text-sm text-gray-600">Based on: {user.email}</p>
              <p className="text-sm text-green-700 font-medium mt-1">Ready to use -- no additional setup required!</p>
            </div>
          )}

          <div className="border border-blue-200 rounded-lg p-5 mb-4">
            <h4 className="font-semibold flex items-center gap-2 mb-3"><Info className="w-5 h-5 text-blue-600" /> How to Use Your Scan Email</h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-semibold mb-1">Sending Emails for Analysis</p>
                <p className="text-gray-600 mb-2">Forward any suspicious email to your scan address. The system will:</p>
                <ul className="list-disc list-inside text-gray-600 space-y-0.5">
                  <li>Process it through Rspamd and SpamAssassin</li>
                  <li>Run AI-powered analysis</li>
                  <li>Save results to your scan history</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-1">Technical Details</p>
                <p className="text-gray-600"><strong>Domain:</strong> <code className="bg-gray-100 px-1 rounded">{SCAN_DOMAIN}</code></p>
                <p className="text-gray-600 mt-1"><strong>Rule:</strong> Replace @ with . in your email, then add @{SCAN_DOMAIN}</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-6"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>
          ) : scanEmails.length === 0 ? (
            <div className="border-2 border-dashed border-green-400 rounded-lg p-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="font-semibold text-green-700 mb-1">You're All Set!</p>
              <p className="text-sm text-gray-500">No additional configuration needed. Your automatic scan email is ready.</p>
            </div>
          ) : (
            <div>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded text-sm mb-3">
                <strong>Legacy scan emails detected.</strong> We recommend using the automatic scan email above.
              </div>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Email</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                      <th className="px-4 py-3 text-left font-semibold">Created</th>
                      <th className="px-4 py-3 text-left font-semibold">Last Used</th>
                      <th className="px-4 py-3 text-center font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {scanEmails.map(e => (
                      <tr key={e.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <code className="text-sm">{e.scan_email}</code>
                            <button onClick={() => copyToClipboard(e.scan_email)} className="text-gray-400 hover:text-blue-600"><Copy className="w-3.5 h-3.5" /></button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {e.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{formatDate(e.created_at)}</td>
                        <td className="px-4 py-3 text-gray-500">{formatDate(e.last_used)}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => toggleScanEmail(e.id, e.is_active)}
                              className={`relative inline-flex h-5 w-9 rounded-full transition ${e.is_active ? 'bg-blue-600' : 'bg-gray-300'}`}>
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition mt-0.5 ${e.is_active ? 'translate-x-4' : 'translate-x-0.5'}`} />
                            </button>
                            <button onClick={() => deleteScanEmail(e.id, e.scan_email)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end mt-3">
            <button onClick={fetchScanEmails} disabled={loading} className="flex items-center gap-1 text-sm text-gray-600 hover:text-blue-600">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-4">
            <SettingsIcon className="w-6 h-6 text-blue-600" />
            <h3 className="text-xl font-semibold">Account</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 block mb-1">Email</span>
              <span className="font-medium">{user?.email || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500 block mb-1">Name</span>
              <span className="font-medium">{user?.name || user?.username || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500 block mb-1">Provider</span>
              <span className="font-medium capitalize">{user?.provider || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500 block mb-1">Role</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${user?.role === 'admin' ? 'bg-red-100 text-red-700' : user?.role === 'collaborator' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                {user?.role || 'user'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Settings;
